import React, { useContext, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { ApplicationContext } from '../../context/GlobalState';
import { t, extractLocaleFromPath } from '../../i18n';
import HreflangTags from '../../components/HreflangTags/HreflangTags';
import './PatientReferral.css';

export default function PatientReferral() {
  const { state } = useContext(ApplicationContext);
  const location = useLocation();
  const { locale } = extractLocaleFromPath(location.pathname);
  const brand = state.brand;
  const baseDomain = brand?.domain || state.client?.domain || 'gaspineortho.com';
  const brandKey = brand?.brandKey || 'gso';

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    referrerName: '',
    lawFirm: '',
    email: '',
    phone: '',
    patientName: '',
    dateOfInjury: '',
    caseType: '',
    injuryDescription: '',
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => { const next = { ...prev }; delete next[field]; return next; });
    }
  };

  const validate = () => {
    const errs = {};
    if (!formData.referrerName.trim()) errs.referrerName = true;
    if (!formData.lawFirm.trim()) errs.lawFirm = true;
    if (!formData.email.trim()) errs.email = true;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errs.email = 'invalid';
    if (!formData.phone.trim()) errs.phone = true;
    if (!formData.patientName.trim()) errs.patientName = true;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    if (!validate()) return;
    setSubmitting(true);
    try {
      const resp = await fetch('/api/request-referral', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brand: brandKey, ...formData }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data.ok === false) {
        setSubmitError(data.error === 'rate_limited' ? 'Too many requests. Please try again in a minute.' : 'Could not send referral. Please try again or call us.');
      } else {
        setSubmitted(true);
      }
    } catch (err) {
      setSubmitError('Network error. Please try again or call us.');
    } finally {
      setSubmitting(false);
    }
  };

  const services = [
    { key: 'diagnosticImaging' },
    { key: 'orthopaedicCare' },
    { key: 'spineCare' },
    { key: 'neurology' },
    { key: 'physicalTherapy' },
    { key: 'painManagement' },
  ];

  const canonicalUrl = `https://${baseDomain}/referral`;
  const pageTitle = `${t('referral.title', locale)} | ${state.client.name}`;
  const pageDesc = t('referral.metaDescription', locale).replace('{{name}}', state.client.name);

  const referralSchema = {
    '@context': 'https://schema.org',
    '@type': 'MedicalBusiness',
    name: state.client.name,
    url: canonicalUrl,
    telephone: state.client.phone,
    makesOffer: services.map(svc => ({
      '@type': 'Offer',
      itemOffered: {
        '@type': 'MedicalTherapy',
        name: t(`referral.service.${svc.key}`, locale),
        description: t(`referral.service.${svc.key}.desc`, locale),
      }
    }))
  };

  return (
    <div className="referral-page">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`https://${baseDomain}/assets/brand/${brandKey}/logo-color.webp`} />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      <HreflangTags baseDomain={baseDomain} pathWithoutLocale="/referral" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(referralSchema) }} />

      <section className="referral-hero glass-dark">
        <div className="container">
          <h1>{t('referral.title', locale)}</h1>
          <p className="referral-hero-brand">{brand.name}</p>
        </div>
      </section>

      <section className="referral-content container">
        <div className="referral-services-grid">
          {services.map(svc => (
            <div key={svc.key} className="referral-service-card glass-card">
              <h3>{t(`referral.service.${svc.key}`, locale)}</h3>
              <p>{t(`referral.service.${svc.key}.desc`, locale)}</p>
            </div>
          ))}
        </div>

        {!submitted ? (
          <form onSubmit={handleSubmit} className="referral-form glass-primary" noValidate>
            <h2>{t('referral.subtitle', locale)}</h2>
            <div className="referral-form-grid">
              <div className={`referral-field${errors.referrerName ? ' field-error' : ''}`}>
                <input
                  className="glass-input"
                  placeholder={t('form.name', locale)}
                  value={formData.referrerName}
                  onChange={e => updateField('referrerName', e.target.value)}
                />
                {errors.referrerName && <span className="error-msg">{t('form.required', locale)}</span>}
              </div>
              <div className={`referral-field${errors.lawFirm ? ' field-error' : ''}`}>
                <input
                  className="glass-input"
                  placeholder={t('referral.lawFirm', locale)}
                  value={formData.lawFirm}
                  onChange={e => updateField('lawFirm', e.target.value)}
                />
                {errors.lawFirm && <span className="error-msg">{t('form.required', locale)}</span>}
              </div>
              <div className={`referral-field${errors.email ? ' field-error' : ''}`}>
                <input
                  className="glass-input"
                  type="email"
                  placeholder={t('form.email', locale)}
                  value={formData.email}
                  onChange={e => updateField('email', e.target.value)}
                />
                {errors.email && (
                  <span className="error-msg">
                    {errors.email === 'invalid' ? t('form.emailInvalid', locale) : t('form.required', locale)}
                  </span>
                )}
              </div>
              <div className={`referral-field${errors.phone ? ' field-error' : ''}`}>
                <input
                  className="glass-input"
                  type="tel"
                  placeholder={t('form.phone', locale)}
                  value={formData.phone}
                  onChange={e => updateField('phone', e.target.value)}
                />
                {errors.phone && <span className="error-msg">{t('form.required', locale)}</span>}
              </div>
              <div className={`referral-field${errors.patientName ? ' field-error' : ''}`}>
                <input
                  className="glass-input"
                  placeholder={t('referral.patientName', locale)}
                  value={formData.patientName}
                  onChange={e => updateField('patientName', e.target.value)}
                />
                {errors.patientName && <span className="error-msg">{t('form.required', locale)}</span>}
              </div>
              <div className="referral-field">
                <input
                  className="glass-input"
                  type="date"
                  placeholder={t('referral.dateOfInjury', locale)}
                  value={formData.dateOfInjury}
                  onChange={e => updateField('dateOfInjury', e.target.value)}
                />
              </div>
            </div>

            <select
              className="glass-input referral-select"
              value={formData.caseType}
              onChange={e => updateField('caseType', e.target.value)}
            >
              <option value="">{t('referral.caseType', locale)}</option>
              <option value="auto">{t('referral.caseAuto', locale)}</option>
              <option value="workers-comp">{t('referral.caseWorkersComp', locale)}</option>
              <option value="slip-fall">{t('referral.caseSlipFall', locale)}</option>
              <option value="other">{t('referral.caseOther', locale)}</option>
            </select>

            <textarea
              className="glass-input referral-textarea"
              placeholder={t('referral.injuryDescription', locale)}
              rows={4}
              value={formData.injuryDescription}
              onChange={e => updateField('injuryDescription', e.target.value)}
            />

            <button type="submit" className="glass-btn glass-btn-accent referral-submit" disabled={submitting}>
              {submitting ? 'Sending…' : t('form.submit', locale)}
            </button>
            {submitError && (
              <div className="referral-form-error" role="alert" style={{ marginTop: 12, color: '#e53e3e', fontSize: 14, textAlign: 'center' }}>
                {submitError}
              </div>
            )}
          </form>
        ) : (
          <div className="referral-success glass-panel">
            <h2>✓ {t('form.success', locale)}</h2>
          </div>
        )}
      </section>
    </div>
  );
}
